import type { CSSProperties } from "react";
import { Toaster as SonnerToaster, type ToasterProps } from "sonner";

import { useWidgetVariant } from "@/contexts/widget-variant";

/** Long enough to read two short lines, short enough not to sit over a spin.
 *  Also drives the cards' progress bar (`--toast-duration`), so the bar and the
 *  dismiss timer can't drift apart. */
const TOAST_DURATION_MS = 5000;

/**
 * The widget's toast layer. Mounted outside the open/closed gate — mission
 * notifications land while the player is in a game with the panel closed, which
 * is exactly when they matter most.
 *
 * Two things about running inside our shadow root:
 *  - sonner self-injects its CSS into `document.head`, which never reaches the
 *    shadow tree, so `src/index.css` imports `sonner/dist/styles.css` and it
 *    rides the same `index.css?inline` injection as everything else.
 *  - `[data-sonner-toaster]` is `position: fixed`, which would escape the
 *    gameframe in-game (the widget host is `position: absolute` inside it) and
 *    the overlay's toast layer over the game edge (the dock shell's `toasts`) —
 *    hence the inline override below, which wins over the stylesheet.
 */
export const Toaster = ({
  position = "top-center",
}: {
  /** Where the stack sits in its container; the overlay's dock picks the
   *  corner nearest the rail. */
  position?: ToasterProps["position"];
}) => {
  const variant = useWidgetVariant();

  return (
    <SonnerToaster
      position={position}
      duration={TOAST_DURATION_MS}
      visibleToasts={3}
      gap={8}
      // Expanded, so a burst reads as N separate cards. Collapsed (sonner's
      // default) is more compact but leaves only ~20px of the card behind
      // showing, which reads as one slab with a shelf rather than two
      // notifications — and there is no hover-to-expand on a phone or in-game.
      expand
      style={{
        position: variant === "on-site" ? "fixed" : "absolute",
        // The widget host is `pointer-events: none` so the page underneath stays
        // usable; the cards opt themselves back in (see `Notification`).
        pointerEvents: "none",
      }}
      toastOptions={{
        // A `toast.custom` toast renders unstyled (sonner sets
        // `data-styled=false` when the toast carries JSX), so there is no
        // default card to fight — but the wrapper loses its width with it,
        // hence `w-full` to fill the toaster's `--width`.
        className: "w-full",
        style: {
          "--toast-duration": `${TOAST_DURATION_MS}ms`,
        } as CSSProperties,
      }}
    />
  );
};
